import { LOCALE_NAMES } from "@repo/shared";
import type { LocaleCode, LocalizedString } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SvgIconField } from "@/components/ui/svg-icon-field";
import { TranslationButton } from "@/components/ui/translation-button";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./risk-option-dialog.i18n";

/** Drop empty/whitespace-only entries from an i18n map. */
function cleanI18n(value: LocalizedString): LocalizedString {
  const cleaned: LocalizedString = {};
  for (const [lang, text] of Object.entries(value)) {
    const trimmed = text?.trim();
    if (trimmed) cleaned[lang as LocaleCode] = trimmed;
  }
  return cleaned;
}

/** Complete i18n maps built by the dialog, ready for the create/update endpoints. */
export type RiskOptionSavePayload = {
  labelI18n: LocalizedString;
  descriptionI18n: LocalizedString | null;
  /** Only present when the icon field is shown (cards display type). */
  iconSvg?: string | null;
};

/** The subset of RiskTimeHorizon/RiskLevel the dialog edits. */
export type RiskOptionValue = {
  labelI18n: LocalizedString;
  descriptionI18n: LocalizedString | null;
  iconSvg: string | null;
};

type RiskOptionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing option when editing, null when creating. */
  option: RiskOptionValue | null;
  /** Show the icon SVG field (cards display type only). */
  showIconField: boolean;
  /** Dialog title, e.g. "Add Time Horizon" / "Edit Risk Level". */
  title: string;
  /** Placeholder for the label input, e.g. "e.g., Short Term (1-2 years)". */
  labelPlaceholder: string;
  /** Persist the option. Should throw on failure so the dialog stays open. */
  onSave: (payload: RiskOptionSavePayload) => Promise<void>;
};

/**
 * QP-180 — shared add/edit dialog for risk-model options (time horizons and
 * risk levels). Edits label + description in the user's language, with a
 * TranslationButton beside each translatable field (standard per-field
 * translation dialog with auto-translate), plus the card icon SVG.
 * Translations are held in local state and persisted on Save.
 */
export function RiskOptionDialog({
  open,
  onOpenChange,
  option,
  showIconField,
  title,
  labelPlaceholder,
  onSave,
}: RiskOptionDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const t = useT(T);

  const [labelI18n, setLabelI18n] = useState<LocalizedString>({});
  const [descriptionI18n, setDescriptionI18n] = useState<LocalizedString>({});
  const [iconSvg, setIconSvg] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Reset drafts each time the dialog opens (for the current option)
  useEffect(() => {
    if (!open) return;
    setLabelI18n({ ...option?.labelI18n });
    setDescriptionI18n({ ...option?.descriptionI18n });
    setIconSvg(option?.iconSvg ?? "");
  }, [open, option]);

  const label = labelI18n[userLanguage as LocaleCode] ?? "";
  const description = descriptionI18n[userLanguage as LocaleCode] ?? "";

  const handleSave = async () => {
    if (!label.trim()) return;

    const cleanedLabel = cleanI18n(labelI18n);
    const cleanedDescription = cleanI18n(descriptionI18n);
    const payload: RiskOptionSavePayload = {
      labelI18n: cleanedLabel,
      // Empty everywhere → clear the description entirely
      descriptionI18n: Object.keys(cleanedDescription).length > 0 ? cleanedDescription : null,
      ...(showIconField && { iconSvg: iconSvg.trim() || null }),
    };

    setIsSaving(true);
    try {
      await onSave(payload);
      onOpenChange(false);
    } catch {
      // Caller surfaces the error; keep the dialog open with the drafts intact.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="risk-option-label">
              {t.labelLabel} ({LOCALE_NAMES[userLanguage as LocaleCode] || userLanguage})
            </Label>
            <div className="flex items-center gap-1">
              <Input
                id="risk-option-label"
                value={label}
                onChange={(e) =>
                  setLabelI18n((prev) => ({ ...prev, [userLanguage]: e.target.value }))
                }
                placeholder={labelPlaceholder}
                autoFocus
              />
              <TranslationButton
                value={labelI18n}
                onSave={(value) => setLabelI18n(value)}
                supportedLanguages={tenantSupportedLanguages}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="risk-option-description">{t.descriptionLabel}</Label>
            <div className="flex items-center gap-1">
              <Input
                id="risk-option-description"
                value={description}
                onChange={(e) =>
                  setDescriptionI18n((prev) => ({ ...prev, [userLanguage]: e.target.value }))
                }
                placeholder={t.descriptionPlaceholder}
              />
              <TranslationButton
                value={descriptionI18n}
                onSave={(value) => setDescriptionI18n(value)}
                supportedLanguages={tenantSupportedLanguages}
                disabled={isSaving}
              />
            </div>
          </div>

          {showIconField && (
            <div className="space-y-2">
              <Label htmlFor="risk-option-icon">{t.iconLabel}</Label>
              <SvgIconField
                id="risk-option-icon"
                value={iconSvg}
                onChange={setIconSvg}
                disabled={isSaving}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogCancel disabled={isSaving}>{t.cancel}</DialogCancel>
          {/* Creating repeats the opener's wording, which is what `title` already holds
              ("Add Time Horizon" / "Add Risk Level"); editing says Save. */}
          <Button type="button" onClick={handleSave} disabled={!label.trim()} loading={isSaving}>
            {option ? t.save : title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
